import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { registerKgSw } from '@cogo/kg-sw'
import './index.css'
import { StudioRoot } from './root'
import { registerDefaults } from '@/features/page-editor/registry/register-defaults'
import { isKgBrowserEnabled } from '@/features/design-system/kg-client'

declare global {
  interface Window {
    /** Installed by the inline boot-trace script in index.html. Optional so a
     *  stripped or cached HTML shell can never break the boot it measures. */
    __cogoMark?: (name: string) => void
  }
}

/**
 * Boot ladder. Read `sessionStorage['cogo-boot-trace']` (or `window.__cogoBoot`)
 * on a blank screen and the last mark says how far it got:
 *
 *   html only          -> this module never evaluated; the import graph stalled
 *   + entry            -> every import of main.tsx resolved
 *   + defaults         -> registry setup returned
 *   + render-call      -> about to hand the tree to React
 *   + render-return    -> React accepted it, so a blank #root after this is a
 *                         render/effect failure, not a boot failure
 */
window.__cogoMark?.('entry')

registerDefaults()
window.__cogoMark?.('defaults')

// Phase 2a Step 7 — register the Cogo KG Service Worker for offline shard
// caching + cross-tab invalidation. Gated behind the same VITE_KG_BROWSER
// feature flag as the kg-browser integration; no-ops when flag is off.
if (isKgBrowserEnabled()) {
  registerKgSw({ strategy: 'on-load', verbose: import.meta.env.DEV }).catch(
    (err: unknown) => console.debug('[main] kg-sw registration error:', err),
  )
}

// F10 (Lane 2) — StudioRoot wraps KgProvider>App in the top-level
// ErrorBoundary (last-resort net; see src/root.tsx).
window.__cogoMark?.('render-call')
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <StudioRoot />
  </StrictMode>,
)
window.__cogoMark?.('render-return')
